const normalize = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

// Mirrors the canonical taxable inflows routed into VET/VTHO/VIP-180 tax tables.
// The audit test compares this registry against backend/logic/tax-routing.js.
export const TAX_TABLE_INCOME_CLASSES = new Set([
  'airdrop',
  'b3mo_challenge_payout_reward',
  'b3mo_split_win_reward',
  'betterswap_pool2earn_rewards',
  'foundation_bonus',
  'foundation_bonus_or_swap',
  'generated',
  'genesis_staking_reward',
  'genesis_staking_rewards',
  'golden_empire_reward',
  'governance_reward',
  'juicyfinance_b3tr_reward_claim',
  'juicyfinance_lending_income',
  'listen2earn_rewards',
  'wanchain_bridge_to_earn_reward',
  'bangzboardz_staking_reward',
  'goatz_milk_nft_holder_reward',
  'mva_staking_rewards',
  'node_legacy_rewards',
  'safehaven_staking_rewards',
  'sagaz_reward_claim',
  'solarwise_electricity_revenue',
  'squad_game_reward',
  'squad_preico_claim',
  'staking_reward',
  'stargate_staking_rewards',
  'thorhead_staking_rewards',
  'vemugs_staking_rewards',
  'vemugs_mint_reward',
  'vemugs_reward_distribution',
  'union_nft_holder_rewards',
  'vaiyans_vco_leaderboard_reward',
  'vebetterdao_mainnet_survey_reward',
  'vedelegate_sponsor_reward',
  'verocket_generated_vtho_claim',
  'vesea_nft_staking_rewards',
  'vestation_reward_claim',
  'vexchange_farm_rewards',
  'vfox_nft_holder_reward',
  'vimworld_veed_reward_distribution',
  'vtho_synthetic',
  'wov_genesis_reward',
  'wov_genesis_staking_reward',
  'wov_genesis_staking_rewards',
  'wov_staking_rewards',
  'nft_creator_royalty',
  'x2earn_reward',
  'x2earn_reward_claim',
]);

// Mirrors backend/logic/fifo-classes.js DISPOSAL_CLASSES. NFT and liquidity
// tables may force the same result explicitly, but token tables must not depend
// on the presence of FIFO detail legs just to display the correct badge.
export const TAX_TABLE_DISPOSAL_CLASSES = new Set([
  'dex_aggregator_execution_cost',
  'dex_aggregator_fee',
  'gas_abstraction_fee',
  'gas_fee',
  'golden_empire_burn_spend',
  'golden_empire_spend',
  'juicyfinance_liquidation_debt_payment',
  'vaultx_loan_repayment_fee',
  'liquidity_add_out',
  'liquidity_remove_lp_out',
  'mugshot_pro_subscription',
  'nfbc_purchase',
  'nft_galaxy_mint',
  'nft_marketplace_purchase_payment',
  'nft_marketplace_pending_mint_payment',
  'nft_mint',
  'nft_transform_disposal',
  'one_million_pixel_purchase',
  'pause_your_carbon_purchase',
  'groncard_gift_card_purchase',
  'sale_payment',
  'scoopup_puppy_pass_fee',
  'vaiyans_vco_game_entry',
  'vaiyans_vco_prediction_entry',
  'velottery_ticket_purchase',
  'vet_domains_registration',
  'vet_domains_renewal',
  'squad_game_spend',
  'squad_vtho_game_entry',
  'squad_nft_upgrade_spend',
  'squad_preico_payment',
  'swap_out',
  'v3tax_purchase',
  'vewin_4ocean_donation',
]);

export function normalizeTaxTreatmentClassification(rowOrClass = '') {
  if (typeof rowOrClass === 'string') return normalize(rowOrClass);
  return normalize(
    rowOrClass?.classification ||
      rowOrClass?.type ||
      rowOrClass?.event ||
      rowOrClass?.kind ||
      rowOrClass?.action ||
      ''
  );
}

export function resolveKnownClassificationTaxTreatment(rowOrClass = '') {
  const cls = normalizeTaxTreatmentClassification(rowOrClass);
  if (TAX_TABLE_INCOME_CLASSES.has(cls)) return 'income';
  if (TAX_TABLE_DISPOSAL_CLASSES.has(cls)) return 'disposal';
  return null;
}

/**
 * Returns undefined when metadata has no opinion, null for explicitly neutral
 * rows, or the canonical taxable result type.
 */
export function resolveRowSemanticTaxTreatment(row = {}) {
  if (row?.taxable === false || row?.excludeFromTaxIncome === true) return null;
  if (row?.isIncome === true || normalize(row?.direction) === 'income') return 'income';
  if (row?.isDisposal === true || normalize(row?.direction) === 'disposal') return 'disposal';

  if (
    row?.isAcquisition === true ||
    row?.isNeutral === true ||
    ['acquisition', 'neutral'].includes(normalize(row?.direction))
  ) {
    return null;
  }

  const semanticValues = [
    row?.taxTreatment,
    row?.tax_treatment,
    row?.cashbackTaxTreatment,
    row?.juicyfinanceTaxTreatment,
    row?.economicClassification,
    row?.economicEventType,
    row?.taxTreatmentHint,
    row?.providerSemanticType,
  ]
    .map(normalize)
    .filter(Boolean);

  if (
    semanticValues.some(
      (value) =>
        value === 'income' ||
        value === 'taxable_income' ||
        value.startsWith('income_') ||
        value.endsWith('_income') ||
        value.includes('revenue_distribution')
    ) ||
    row?.incomeSource ||
    row?.revenueSource
  ) {
    return 'income';
  }

  if (
    semanticValues.some(
      (value) =>
        value === 'disposal' ||
        value === 'taxable_disposal' ||
        value.includes('capital_gain')
    )
  ) {
    return 'disposal';
  }

  if (
    semanticValues.some((value) =>
      [
        'non_taxable',
        'non_taxable_transform',
        'lot_preserving_non_disposal',
        'purchase_cashback',
        'certificate',
        'borrowing_interest_info_only',
        'non_taxable_loan_principal',
      ].some((neutral) => value === neutral || value.startsWith(`${neutral}_`))
    )
  ) {
    return null;
  }

  return undefined;
}
